import { Compass, LocateFixed, Maximize2, Minimize2, ScanEye, Spline, Video } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type maplibregl from 'maplibre-gl';
import type { CooperativeTraffic, DroneMapMission, RcController, SiteMarker, MapStyleKey, VideoBesideControl } from './droneMapTypes';
import { MapLayersMenu } from './MapLayersMenu';
import { MapLegend, type HandoverKeyState } from './MapLegend';
import { MapScaleBar } from './MapScaleBar';
import { MAP_CONTROL } from '@/lib/copy';

interface DroneMapViewState {
  airspace: boolean;
  missions: boolean;
  altitudeLabels: boolean;
  /**
   * Still here after the rings' key left this column for the aircraft's
   * right-click menu (amendment B): the map KEY needs to know whether they are
   * drawn, or it lists a swatch for ink that is not on the map. It is a legend
   * input now, not a control state.
   */
  rangeRings: boolean;
}

/**
 * FOLLOW and TRACK, for the aircraft that is currently selected.
 *
 * They lived on DroneStateCard's button row until the card was replaced by
 * DroneSelectionChip. They belong here rather than on the chip or in the
 * command bar for one reason: neither of them commands the aircraft. FOLLOW
 * moves the camera and TRACK draws a line — that is map view state, and this
 * cluster is where every other map view toggle already lives.
 *
 * Null when nothing is selected, which is why the pair is rendered at the BOTTOM
 * of the control column rather than the top: selecting an aircraft grows the
 * column downward instead of pushing the eight persistent controls down by two
 * button heights. Put it above them and an operator resting on the compass while
 * clicking a drone in the strip board finds Airspace under the cursor instead,
 * and the next click toggles the wrong layer.
 */
export interface SelectedDroneMapControls {
  callsign: string;
  isFollowing: boolean;
  isTrailShown: boolean;
  onToggleFollow: () => void;
  onToggleTrail: () => void;
  /**
   * VIDEO: this aircraft's picture beside the map, half the stage each (#505
   * note 4). Selection-scoped like its two neighbours, and for the same reason:
   * it commands nothing, it changes what the operator is looking at. Absent or
   * null, no key - a deployment without the switch draws exactly the column it
   * drew before.
   */
  videoBeside?: VideoBesideControl | null;
}

interface DroneMapControlsProps {
  mapStyle: MapStyleKey;
  /** The live MapLibre instance, for the scale bar. Null until it is built. */
  map: maplibregl.Map | null;
  isFullscreen: boolean;
  /**
   * The camera is off north-up, so Reset north has something to reset (D11).
   *
   * The key is not drawn otherwise. On a north-up map - which is the map nearly
   * all of the time - pressing it did nothing, and it was the FIRST key in the
   * column, so the one control that never had an effect was the one an operator
   * reached first.
   */
  isRotated: boolean;
  viewState: DroneMapViewState;
  onResetNorth: () => void;
  onGoToMyLocation: () => void;
  onToggleAirspace: () => void;
  onToggleMissions: () => void;
  onToggleAltitudeLabels: () => void;
  onSelectStyle: (key: MapStyleKey) => void;
  satelliteAvailable: boolean;
  onToggleFullscreen: () => void;
  /** Opens the zones sheet, from the Layers menu. Absent for a viewer. */
  onOpenZones?: () => void;
  /**
   * Chrome floating over the map's bottom edge, in CSS px.
   *
   * The map key and the scale bar lift by this much instead of being covered by
   * it. Zero in the classic shell.
   */
  bottomInsetPx?: number;
  /**
   * Chrome floating over the map's RIGHT edge, in CSS px.
   *
   * This whole column is anchored at `right-3`, and the COP's right panel is
   * 288 to 560 px wide. Until the panel floated it could not reach the column,
   * because opening it narrowed the map; now it covers the column outright, and
   * with it Layers, fullscreen, follow, track and the video key. Zero when
   * nothing floats over the right edge.
   */
  rightInsetPx?: number;
  /**
   * Chrome floating over the map's LEFT edge, in CSS px.
   *
   * The map key and the scale-bar row are anchored left, and the COP's
   * navigation rail floats over that edge. The key's trigger is a deliberate
   * 44 px focusable target, so leaving it under the rail fails WCAG 2.5.8 and
   * 2.4.11 as well as hiding it.
   */
  leftInsetPx?: number;
  /**
   * Chrome floating over the map's TOP edge, in CSS px.
   *
   * `CopTopStack` (#505 slice 7) holds the dock roster and the manual session
   * strip. Both used to be rows ABOVE the map, so this column's top edge was
   * never contested; now they float over it at `z-30` against this column's
   * `z-20`, and the roster is present on any COP that has docks - which is to
   * say, on the golden flow's own screen. Without this, Layers, fullscreen,
   * follow, track and the video key are covered on every load, which is exactly
   * what `rightInsetPx` was added for on the other edge.
   */
  topInsetPx?: number;
  missions: DroneMapMission[];
  trailCount: number;
  cursorCoords: string | null;
  controllers: RcController[];
  cooperativeTraffic: CooperativeTraffic[];
  sites: SiteMarker[];
  dockCount: number;
  triggerCount: number;
  /** Optional: a map with no coverage handovers (the manual minimap, tests) keys none. */
  handoverKey?: HandoverKeyState;
  selectedDrone?: SelectedDroneMapControls | null;
}

export function DroneMapControls({
  mapStyle,
  map,
  isFullscreen,
  isRotated,
  viewState,
  onResetNorth,
  onGoToMyLocation,
  onToggleAirspace,
  onToggleMissions,
  onToggleAltitudeLabels,
  onSelectStyle,
  satelliteAvailable,
  onToggleFullscreen,
  onOpenZones,
  bottomInsetPx = 0,
  rightInsetPx = 0,
  leftInsetPx = 0,
  topInsetPx = 0,
  missions,
  trailCount,
  cursorCoords,
  controllers,
  cooperativeTraffic,
  sites,
  dockCount,
  triggerCount,
  handoverKey,
  selectedDrone,
}: DroneMapControlsProps) {
  const fullscreenRef = useRef<HTMLButtonElement>(null);
  /** The north key holds focus right now, so its removal would strand it. */
  const northHasFocusRef = useRef(false);

  /**
   * Reset north unmounts ITSELF: pressing it eases the camera to bearing 0,
   * which clears `isRotated`, which removes the button the operator is standing
   * on. A keyboard operator was left with focus on `document.body` and a Tab
   * that restarted from the top of the page.
   */
  useEffect(() => {
    if (isRotated || !northHasFocusRef.current) return;
    northHasFocusRef.current = false;
    fullscreenRef.current?.focus();
  }, [isRotated]);

  return (
    <>
      {/* z-20: on short viewports this stack can grow tall enough to reach the
          bottom-right corner, and navigation controls must always win pointer
          capture over anything drawn there at a lower layer, never the reverse.
          Found via T3.1's map-style-toggle test, against the weather panel that
          used to sit at bottom-14 right-3 z-10: without this, the Toggle Map
          Style button silently ate every click meant for it. The panel has since
          been cut, but the rule it exposed is the reason this stays z-20. */}
      {/* Capped and scrollable, because selecting an aircraft now grows this
          column by a divider and two 44px buttons (~101px) while the very same
          selection renders DroneCommandBar below the map and SHRINKS the pane.
          DroneMap's root is `overflow-hidden`, so anything past the bottom edge
          is not clipped-and-reachable, it is gone — and because the pair is
          appended last, FOLLOW and TRACK are the first things to vanish. On a
          1366x768 field laptop that put them off-screen entirely, with nothing
          on screen saying the right-click menu still has them.
          `[&>*]:shrink-0` keeps the buttons at 44px instead of letting flex
          compress them once a max-height is in play. */}
      <div
        className="absolute top-3 right-3 z-20 p-0.5 flex flex-col gap-1 max-h-[calc(100%-1.5rem)] overflow-y-auto overscroll-contain [&>*]:shrink-0"
        // Pushed clear of whatever floats over the map's right and top edges,
        // the same way the bottom-left stack is lifted clear of the bottom.
        // `right-3` and `top-3` stay in the class list and are only overridden
        // when something is actually in the way, so with nothing floating this
        // keeps its rem-based offsets under text zoom.
        //
        // The max-height goes with the top offset. This column already caps
        // itself at `calc(100% - 1.5rem)` and scrolls, so pushing it down
        // without taking the same height away would run its last keys off the
        // bottom of the map - and the keys at the bottom of it are the
        // selection-scoped group: follow, track and video.
        style={{
          ...(rightInsetPx > 0 ? { right: `calc(0.75rem + ${rightInsetPx}px)` } : null),
          ...(topInsetPx > 0
            ? {
                top: `calc(0.75rem + ${topInsetPx}px)`,
                maxHeight: `calc(100% - 1.5rem - ${topInsetPx}px)`,
              }
            : null),
        }}
      >
        <button type="button" onClick={onGoToMyLocation} title={MAP_CONTROL.myLocation} className="flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors"><LocateFixed className="size-5" strokeWidth={1.5} /></button>
        <MapLayersMenu
          airspace={viewState.airspace}
          missions={viewState.missions}
          altitudeLabels={viewState.altitudeLabels}
          mapStyle={mapStyle}
          onToggleAirspace={onToggleAirspace}
          onToggleMissions={onToggleMissions}
          onToggleAltitudeLabels={onToggleAltitudeLabels}
          onSelectStyle={onSelectStyle}
          satelliteAvailable={satelliteAvailable}
          onOpenZones={onOpenZones}
        />
        <button ref={fullscreenRef} type="button" onClick={onToggleFullscreen} title={MAP_CONTROL.fullscreen} className="flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors">{isFullscreen ? <Minimize2 className="size-5" strokeWidth={1.5} /> : <Maximize2 className="size-5" strokeWidth={1.5} />}</button>
        {/* LAST of the persistent keys, not first, even though north-up is where
            the map starts and the compass reads like a header.

            This key is CONDITIONAL (D11), and this column is anchored at the
            top, so a conditional key placed first pushes every key below it down
            44 px at the moment it appears - which is the middle of the rotate
            gesture that made it appear. The operator twists the map and My
            location, Layers and Fullscreen all slide under their own finger,
            then slide back the instant Reset north is pressed. It is the same
            argument the selection-scoped pair below is built on, and it is why
            that pair is appended rather than prepended. */}
        {isRotated && (
          <button
            type="button"
            onFocus={() => { northHasFocusRef.current = true; }}
            onBlur={() => { northHasFocusRef.current = false; }}
            onClick={onResetNorth}
            title={MAP_CONTROL.resetNorth} className="flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors"><Compass className="size-5" strokeWidth={1.5} /></button>
        )}
        {selectedDrone && (
          <>
            {/* Separates the two selection-scoped toggles from the persistent
                map controls, so the group reads as belonging to the aircraft
                rather than to the map. */}
            <span aria-hidden className="my-0.5 h-px w-full bg-divider" />
            {/* `ScanEye`, not `Crosshair`. Follow sits four rows under My
                Location, whose `LocateFixed` is a crosshair with a dot — at
                44px, two crosshairs in one column is a recentre-on-yourself
                waiting to happen while an aircraft is being watched.
                `aria-label` as well as `title`: `title` is a last-resort
                accessible name and renders nothing at all on touch, which is
                the RC screen this pair now has to work on. */}
            <button
              type="button"
              onClick={selectedDrone.onToggleFollow}
              aria-pressed={selectedDrone.isFollowing}
              aria-label={
                selectedDrone.isFollowing
                  ? MAP_CONTROL.stopFollowing(selectedDrone.callsign)
                  : MAP_CONTROL.follow(selectedDrone.callsign)
              }
              title={
                selectedDrone.isFollowing
                  ? MAP_CONTROL.stopFollowing(selectedDrone.callsign)
                  : MAP_CONTROL.follow(selectedDrone.callsign)
              }
              className={
                selectedDrone.isFollowing
                  ? 'flex items-center justify-center size-11 rounded-md bg-status-blue-tint border border-status-blue/40 text-status-blue-fg'
                  : 'flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors'
              }
            >
              <ScanEye className="size-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={selectedDrone.onToggleTrail}
              aria-pressed={selectedDrone.isTrailShown}
              aria-label={
                selectedDrone.isTrailShown
                  ? MAP_CONTROL.hideTrack(selectedDrone.callsign)
                  : MAP_CONTROL.showTrack(selectedDrone.callsign)
              }
              title={
                selectedDrone.isTrailShown
                  ? MAP_CONTROL.hideTrack(selectedDrone.callsign)
                  : MAP_CONTROL.showTrack(selectedDrone.callsign)
              }
              className={
                selectedDrone.isTrailShown
                  ? 'flex items-center justify-center size-11 rounded-md bg-status-blue-tint border border-status-blue/40 text-status-blue-fg'
                  : 'flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors'
              }
            >
              <Spline className="size-5" strokeWidth={1.5} />
            </button>
            {selectedDrone.videoBeside && (
              /* Last in the column, after the pair, so a deployment that turns
                 the switch on grows the column by one key at the bottom and
                 moves nothing an operator already reaches for. */
              <button
                type="button"
                onClick={selectedDrone.videoBeside.onToggle}
                aria-pressed={selectedDrone.videoBeside.on}
                aria-label={
                  selectedDrone.videoBeside.on
                    ? 'Hide the picture beside the map'
                    : `Show ${selectedDrone.callsign}'s picture beside the map`
                }
                title={
                  selectedDrone.videoBeside.on
                    ? 'Hide the picture beside the map'
                    : `Show ${selectedDrone.callsign}'s picture beside the map`
                }
                className={
                  selectedDrone.videoBeside.on
                    ? 'flex items-center justify-center size-11 rounded-md bg-status-blue-tint border border-status-blue/40 text-status-blue-fg'
                    : 'flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors'
                }
              >
                <Video className="size-5" strokeWidth={1.5} />
              </button>
            )}
          </>
        )}
      </div>

      {/* Bottom-left corner. The map key and the scale bar are one pill now,
          not a column of two - the key owns the chrome and the scale bar is
          passed in as the reading beside its button, so nothing here has to
          reconcile two heights. The corner's collapsed height (28px pill + 12px
          inset = 40px) is what AirspacePanel's bottom-11 clears.

          pointer-events-none, and every actual control opts back in on itself
          (MapLegend's button and panel). The wrapper is hit-testable across its
          child's box, and that transparent margin was live map - drag-to-pan,
          click-to-place in dispatch/plan/zone-draw, right-click dispatch.
          Deliberately NOT a child-blanket variant re-arming every direct child:
          that hands back a child's whole box, including the ~320x28px of empty
          space beside the panel when the key is expanded, and it silently
          outranks any child that later opts out - the blanket rule and
          `.pointer-events-none` are both specificity (0,1,0), so the wrapper
          wins on source order, with no error and no lint warning. (Written
          without the literal class name on purpose: Tailwind scans this file as
          plain text, so spelling it out here emits the rule.) */}
      <div
        className="pointer-events-none absolute bottom-3 left-3 z-20 flex flex-col items-start"
        // Lifted out from under whatever floats over the map's bottom edge. The
        // map key's trigger is a 28 px target and a focusable control; leaving
        // it half under a bar fails both WCAG 2.5.8 and 2.4.11. Zero in the
        // classic shell, where nothing floats.
        style={{
          ...(bottomInsetPx > 0 ? { bottom: `calc(0.75rem + ${bottomInsetPx}px)` } : null),
          ...(leftInsetPx > 0 ? { left: `calc(0.75rem + ${leftInsetPx}px)` } : null),
        }}
      >
        <MapLegend
          viewState={viewState}
          missions={missions}
          trailCount={trailCount}
          controllers={controllers}
          cooperativeTraffic={cooperativeTraffic}
          sites={sites}
          dockCount={dockCount}
          triggerCount={triggerCount}
          handoverKey={handoverKey}
        >
          <MapScaleBar map={map} cursorCoords={cursorCoords} />
        </MapLegend>
      </div>

      {/* The count strip that stood at `top-3 left-16` is gone (slice 8's cut
          list). Every field on it was on screen twice: FLYING / TRACKED is the
          strip board immediately below it, MISSIONS is the badge on the top
          bar's Missions key, and TRAILS is a map-key fact. The coordinates were
          the one thing it said that nothing else did, and they are in the
          scale-bar row above.

          It also sat directly over the top-left corner of the map at a fixed
          `left-16`, which is where the strip board's own header and the floating
          rail's expansion both land - three surfaces claiming one corner. */}
    </>
  );
}
